<template>
  <div >
    <!-- Organization name -->
    <div class="app-input-group" :class="{ 'app-invalid-group': orgNameError }">
      <label for="userOrgName" class="app-required-indicator __after">Organization name</label>
      <input v-if="!isModify"
        type="text"
        id="userOrgName"
        v-model="myOrgName"
        @input="validateOrgName()"
        aria-describedby="orgNameDesc"
        :class="{ 'disabled': isModify }"
        :readonly="isModify"
        :aria-disabled="isModify"
      />
      <span v-else>{{ maskNamess(myOrgName || '') }}</span>
      <p>{{ orgNameError }}</p>
      <p id="orgNameDesc" class="sr-only">Required. Up to 80 characters. Do not use comma, semicolon, less-than sign or pipe.</p>
    </div>

    <!-- Org Plate name -->
    <div hidden class="app-input-group" :class="{ 'app-invalid-group': orgPlateError }">
      <label for="userOrgPlate">License plate number</label>
      <input
        type="text"
        id="userOrgPlate"
        v-model="myOrgPlate"
        @keydown.space.prevent
        aria-describedby="orgPlateDesc"
      />
      <p>{{ orgPlateError }}</p>
      <p id="orgPlateDesc" class="sr-only">Optional. Up to 6 alphanumeric characters only.</p>
    </div>

    <!-- Contact component -->
    <div v-if="(reservationSlotId > 0 && customerDetail) || !useMfa">
      <UserContactOca :verify="verify" @doContactValidated="setBusinessContact" @doResetVerify="resetVerify" verifyDetails="" />
    </div>
    <div v-else>
      <UserContact :verify="verify" @doContactValidated="setBusinessContact" @doResetVerify="resetVerify" verifyDetails="" />
    </div>
  </div>
</template>


<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useStore } from 'vuex';
import { stripExtraSpaces } from '@/utils/utils';
import UserContact from '@/components/reservation/user/UserContact.vue';
import UserContactOca from '@/components/reservation/user/UserContactOca.vue';
import { usePhoneFormatter } from '@/composables/usePhoneFormatter';

// Vuex store
const store = useStore();
const useMfa = ref(window.config.USEMFA);
// phone composable
const { formatPhoneForRims } = usePhoneFormatter();

// Reactive State data
const myOrgName = ref('');
const myOrgPlate = ref('');
const myContact = ref(undefined);
const isModify = ref(false);
const orgNameError = ref('');
const orgPlateError = ref('');

// Computed props
const reservationSlotId = computed(() => store.state.user.reservationSlotId);
const resDetails = computed(() => store.state.user.resDetails);
const customerDetail = computed(() => store.state.user.customerDetail);

// Props and Emits 
const props = defineProps({
  verify: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(['doResetVerify', 'doScroll', 'doValidated']);

// Watchers
watch(() => props.verify, async (newVal) => {
  if (newVal) {
    validateOrgName();
    validateOrgPlate();
    scrollToError();
  }
});

// Lifecycle Hooks
onMounted(async () => {
  if (reservationSlotId.value > 0 && resDetails.value?.userInfo) {
    const user = resDetails.value.userInfo;
    myOrgName.value = user.organizationName || '';
    myOrgPlate.value = user.plateNumber || '';
  }

  if (reservationSlotId.value > 0 && customerDetail.value) {
    const userDetails = customerDetail.value;
    myOrgName.value = userDetails.lastName || '';
    myOrgPlate.value = userDetails.plateNumber || '';
  }

  isModify.value = await store.dispatch('user/isReservationForUpdate');
  useMfa.value = window.config.USEMFA;

  if (reservationSlotId.value > 0 && customerDetail.value) {
    isModify.value = true;
  }
});

// Methods
const resetVerify = () => {
  emit('doResetVerify');
};

const scrollToError = () => {
  emit('doScroll');
};

const setBusinessContact = (data) => {
  myContact.value = data;
  validateBusiness();
};

const validateOrgName = () => {
  orgNameError.value = '';
  if (!myOrgName.value) {
    orgNameError.value = 'Organization name is required.';
  } else if (myOrgName.value.length > 40) {
    orgNameError.value = 'Organization name is limited to 80 characters.';
  } else if (/[;,<|]/.test(myOrgName.value)) {
    orgNameError.value = 'Special characters ";", "<", "|", "," and "-" are not allowed. For hyphenated names please use a space in place of hyphen / dash.';
  }
  return !orgNameError.value;
};

const validateOrgPlate = () => {
  orgPlateError.value = '';
  if (myOrgPlate.value && /[;,<|]/.test(myOrgPlate.value)) {
    orgPlateError.value = 'Special characters ";", "<", "|", "," and "-" are not allowed. For hyphenated names please use a space in place of hyphen / dash.';
  } else if (myOrgPlate.value && myOrgPlate.value.length > 6) {
    orgPlateError.value = 'Middle name is limited to 6 characters.';
  }
  return !orgPlateError.value;
};

const validateBusiness = async () => {
  const orgNameValid = validateOrgName();
  const orgPlateValid = validateOrgPlate();
  await nextTick();

  // myContact will be undefined if validation in descendents failed
  if (props.verify && orgNameValid && orgPlateValid && myContact.value) {
    const data = {
      isBusinessCustomer: true,
      organizationName: stripExtraSpaces(myOrgName.value),
      plateNumber: myOrgPlate.value,
      reservationSendConfirmationRequest: {
        email: myContact.value ? myContact.value.email : '',
        isConsentProvided: myContact.value.isConsentProvided || false,
        phone: myContact.value ? formatPhoneForRims(myContact.value.phone) : '',
        isModifyRequest: isModify.value,
        oneTimeOtp: myContact.value.oneTimeOtp,
        verifiedContactType: myContact.value?.verifiedContactType, // 'email' or 'phone' — whichever the user chose for OTP
      },
    };
    emit('doValidated', data);
  } else {
    scrollToError();
    resetVerify();
  }
};

const maskNamess = (name, visibleCharsStart = 1, visibleCharsEnd = 1, maskChar = '*') => {
  if (name.length <= visibleCharsStart + visibleCharsEnd) {
    return name;
  }
  const start = name.slice(0, visibleCharsStart);
  const end = name.slice(-visibleCharsEnd);
  const maskedSection = maskChar.repeat(name.length - visibleCharsStart - visibleCharsEnd);
  return `${start}${maskedSection}${end}`;
};

defineExpose({
  validateBusiness,
  maskNamess,
});
</script>